import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Users, Clock, Calendar, Award, GraduationCap, Briefcase, CheckCircle, AlertCircle, MoreHorizontal, Edit, Trash2, AlertTriangle, Archive, ArchiveRestore } from 'lucide-react';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const EmployeeList = ({ employees: tous, onEditEmployee, onDeleteEmployee, onRestoreEmployee }) => {
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  // Les salariés archivés (« supprimés » de l'équipe) gardent leur fiche et leurs
  // heures passées ; on les cache par défaut, un interrupteur les montre.
  const [voirArchives, setVoirArchives] = useState(false);
  const archives = tous.filter((e) => e.archivedAt);
  const employees = voirArchives ? archives : tous.filter((e) => !e.archivedAt);
  const getContractStatus = (contractEndDate) => {
    if (!contractEndDate) return { status: 'cdi', label: 'CDI', color: 'bg-green-100 text-green-800' };
    
    const endDate = new Date(contractEndDate);
    const today = new Date();
    const warningDate = addDays(today, 30); // Alerte 30 jours avant
    
    if (isBefore(endDate, today)) {
      return { status: 'expired', label: 'Expiré', color: 'bg-red-100 text-red-800' };
    } else if (isBefore(endDate, warningDate)) {
      return { status: 'warning', label: 'Expire bientôt', color: 'bg-orange-100 text-orange-800' };
    } else {
      return { status: 'active', label: 'CDD', color: 'bg-blue-100 text-blue-800' };
    }
  };

  const handleDeleteEmployee = () => {
    if (deleteEmployeeId) {
      onDeleteEmployee(deleteEmployeeId);
      setDeleteEmployeeId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      assigned: 'bg-blue-100 text-blue-800',
      unavailable: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.available;
  };

  const getStatusIcon = (status) => {
    const icons = {
      available: <CheckCircle className="w-3 h-3" />,
      assigned: <Users className="w-3 h-3" />,
      unavailable: <AlertCircle className="w-3 h-3" />
    };
    return icons[status] || icons.available;
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'Disponible',
      assigned: 'Assigné',
      unavailable: 'Indisponible'
    };
    return labels[status] || status;
  };

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center text-blue-800">
          <Users className="w-5 h-5 mr-2" />
          {voirArchives
            ? `Anciens salariés (${employees.length})`
            : `Équipe (${employees.length} salarié${employees.length > 1 ? 's' : ''})`}
        </CardTitle>
        {archives.length > 0 && (
          <button
            onClick={() => setVoirArchives((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline self-start"
          >
            {voirArchives ? "← Revenir à l'équipe" : `Voir les anciens salariés (${archives.length})`}
          </button>
        )}
      </CardHeader>
      <CardContent>
        {employees.length === 0 ? (
          <div className="text-center py-8 text-blue-600">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">{voirArchives ? 'Aucun ancien salarié' : 'Aucun salarié'}</p>
            {!voirArchives && <p className="text-sm">Créez votre première équipe pour commencer</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {employees.map(employee => (
              <div
                key={employee.id}
                className={`p-4 border border-blue-100 rounded-lg hover:bg-blue-50 transition-colors ${employee.active === false ? 'opacity-60 bg-gray-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-blue-900">{employee.fullName}</h3>
                      {employee.contractEndDate && (
                        <Badge className={getContractStatus(employee.contractEndDate).color}>
                          {getContractStatus(employee.contractEndDate).status === 'warning' && (
                            <AlertTriangle className="w-3 h-3 mr-1" />
                          )}
                          {getContractStatus(employee.contractEndDate).label}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-blue-700">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {employee.weeklyHours}h/semaine
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {employee.annualLeave} j. congés
                      </div>
                    </div>
                    
                    <div className="mt-2 text-xs text-blue-600 space-y-1">
                      <div><strong>Jours de travail:</strong> {employee.workDays.join(', ')}</div>
                      <div><strong>Heures vendables/an:</strong> {employee.annualSellableHours}h</div>
                      {employee.contractStartDate && (
                        <div className={new Date(employee.contractStartDate) > new Date() ? 'text-orange-600' : 'text-blue-600'}>
                          <strong>Début de contrat:</strong> {format(new Date(employee.contractStartDate), 'dd/MM/yyyy', { locale: fr })}
                          {new Date(employee.contractStartDate) > new Date() && ' — pas encore en poste'}
                        </div>
                      )}
                      {employee.contractEndDate && (
                        <div className={`${getContractStatus(employee.contractEndDate).status === 'expired' ? 'text-red-600' : getContractStatus(employee.contractEndDate).status === 'warning' ? 'text-orange-600' : 'text-blue-600'}`}>
                          <strong>Fin de contrat:</strong> {format(new Date(employee.contractEndDate), 'dd/MM/yyyy', { locale: fr })}
                        </div>
                      )}
                      {employee.isApprentice && employee.trainingWeeks && employee.trainingWeeks.length > 0 && (
                        <div className="text-yellow-600">
                          <strong>Semaines formation:</strong> {employee.trainingWeeks.length}
                        </div>
                      )}
                      {employee.productivityCoefficient !== undefined && employee.productivityCoefficient !== null && employee.productivityCoefficient !== 1 && (
                        <div className="text-yellow-600">
                          <strong>Heures pondérées:</strong> {Math.round(employee.productivityCoefficient * 100)}% du rendement standard
                        </div>
                      )}
                      {employee.productionPresenceCoefficient != null && employee.productionPresenceCoefficient !== 1 && (
                        <div className="text-yellow-600">
                          <strong>Présence en production :</strong>{' '}
                          {Math.round(employee.productionPresenceCoefficient * 100)}% de son temps sur les chantiers
                          <span className="text-gray-500"> (planning seulement)</span>
                        </div>
                      )}
                      {employee.skills && employee.skills.length > 0 && (
                        <div>
                          <strong>Spécialités:</strong> {employee.skills.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end space-y-2">
                    <div className="flex items-center space-x-2">
                      <Badge className={employee.active === false ? 'bg-gray-300 text-gray-700' : getStatusColor(employee.status)}>
                        {employee.active === false ? <AlertCircle className="w-3 h-3" /> : getStatusIcon(employee.status)}
                        <span className="ml-1">{employee.active === false ? 'Inactif' : getStatusLabel(employee.status)}</span>
                      </Badge>
                      
                      {/* Menu d'actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditEmployee(employee)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          {employee.archivedAt ? (
                            <DropdownMenuItem onClick={() => onRestoreEmployee(employee.id)}>
                              <ArchiveRestore className="w-4 h-4 mr-2" />
                              Réactiver
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => setDeleteEmployeeId(employee.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Retirer de l'équipe
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="flex space-x-1">
                      {employee.isQualified && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          <Award className="w-3 h-3 mr-1" />
                          Qualifié
                        </Badge>
                      )}
                      {employee.isApprentice && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                          <GraduationCap className="w-3 h-3 mr-1" />
                          Apprenti
                        </Badge>
                      )}
                      {employee.isSubcontractor && (
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          <Briefcase className="w-3 h-3 mr-1" />
                          Sous-traitance
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {employee.currentAssignments && employee.currentAssignments.length > 0 && (
                  <div className="mt-3 bg-gray-50 p-2 rounded text-xs">
                    <strong className="text-gray-700">Assigné à:</strong>
                    <div className="mt-1">
                      {employee.currentAssignments.map((assignment, index) => (
                        <span key={index} className="inline-block bg-gray-200 text-gray-700 px-2 py-1 rounded mr-1">
                          {assignment}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteEmployeeId !== null} onOpenChange={() => setDeleteEmployeeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer le salarié de l'équipe</AlertDialogTitle>
            <AlertDialogDescription>
              Il n'apparaîtra plus dans l'équipe ni dans le planning, et ses chantiers seront
              réaffectés. Sa fiche et ses heures passées sont conservées : il reste visible
              dans « anciens salariés », d'où il peut être réactivé.
              {deleteEmployeeId && (
                <span className="block mt-2 font-medium">
                  {tous.find(emp => emp.id === deleteEmployeeId)?.fullName}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmployee} className="bg-red-600 hover:bg-red-700">
              Retirer de l'équipe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EmployeeList;